import _get from 'lodash/get';
import _set from 'lodash/set';
import axios, { AxiosError } from 'axios';
import React, { useCallback, useContext } from 'react';
import { DataContext } from '../components/DataContext';
import { isArray } from '../helpers';
import { useAuthToken } from './useAuthRequest';

interface SendFileRequestResponse {
	fileUniqueId: string;
}

type SendFileRequestError = AxiosError;

interface FileValue {
	uniqueId?: string;
	path: string;
	type: string;
	fileName: string;
	fileLength: number;
}

interface FileWithContents extends FileValue {
	content?: Blob;
}

type Progress = Record<string, number>;

export interface FileArgs {
	domain?: string;
	endpoint: string | Function;
	xAPIKey?: string;
	fieldName?: string;
	onStart?: (files: FileWithContents[]) => void;
	onProgress?: (file: FileWithContents, progress: Progress) => void;
	onUpload?: (file: FileWithContents) => void;
	onError?: (file: FileWithContents, e: any) => void;
	onComplete?: () => void;
}

export function useFileRequest(args: FileArgs) {
	const { domain: contextDomain, domains, apiVersion = 1, restClient } = useContext(DataContext);
	const { set, get } = useAuthToken();

	const getRequestArgs = (override?: Partial<FileArgs>) => {
		const merged = {
			...args,
			...(override || {}),
		};
		const { domain = contextDomain, endpoint, fieldName, xAPIKey, onProgress } = merged;
		const { base, xAPIKey: contextXAPIKey } = domains[domain];
		let newEndpoint = endpoint;
		if (typeof endpoint !== 'string') {
			newEndpoint = endpoint(fieldName);
		}

		return {
			base,
			domain,
			endpoint: (newEndpoint as string).replace(`{ver}`, `v${apiVersion}`),
			xAPIKey: xAPIKey || contextXAPIKey,
			onProgress,
		};
	};

	const downloadFile = useCallback(
		(file: Pick<FileValue, 'path' | 'fileName'>, override?: Partial<FileArgs>): Promise<void> => {
			const { base, xAPIKey } = getRequestArgs(override);

			return new Promise((resolve, reject) => {
				const headers = {
					'Content-Type': 'multipart/form-data',
					'x-api-key': xAPIKey || '',
				} as Record<string, string>;
				const tokens = get();
				if (tokens) {
					headers.Authorization = `Bearer ${tokens.token}`;
				}
				const path = file.path
					? (file.path.indexOf('http') === 0 ? file.path : `${base}${file.path}`).replace(
							`{ver}`,
							`v${apiVersion}`
					  )
					: undefined;
				restClient!
					.get(`${path}`, {
						headers,
						responseType: 'blob',
					})
					.then((response) => {
						const url = URL.createObjectURL(new Blob([response.data]));
						const link = document.createElement('a');
						link.href = url;
						link.setAttribute('download', file.fileName);
						link.click();
						resolve();
					})
					.catch((error: SendFileRequestError) => {
						reject(error.response);
					});
			});
		},
		[]
	);

	const sendFile = useCallback(
		(file: FileWithContents, override?: Partial<FileArgs>): Promise<SendFileRequestResponse> => {
			const { base, endpoint, xAPIKey, onProgress } = getRequestArgs(override);
			const data = new FormData();
			if (file.content) {
				data.append('formFile', file.content, file.fileName);
			}

			return new Promise((resolve, reject) => {
				const headers = {
					'Content-Type': 'multipart/form-data',
					'x-api-key': xAPIKey || '',
				} as Record<string, string>;
				const tokens = get();
				if (tokens) {
					headers.Authorization = `Bearer ${tokens.token}`;
				}
				restClient!
					.post<SendFileRequestResponse>(`${base}${endpoint}`, data, {
						headers,
						onUploadProgress: (progressEvent: Progress) => {
							progressEvent.percent = (progressEvent.loaded / progressEvent.total) * 100;
							if (onProgress) onProgress(file, progressEvent);
						},
					})
					.then((response) => {
						resolve(response.data);
					})
					.catch((error: SendFileRequestError) => {
						console.log({ error });
						reject(error.response);
					});
			});
		},
		[]
	);

	/***
	 * @deprecated
	 * now you have to pass null as form field value to delete file
	 */
	const removeFile = useCallback((file: FileWithContents, override?: Partial<FileArgs>) => {
		const { base, endpoint, xAPIKey, onProgress } = getRequestArgs(override);
		return new Promise((resolve, reject) => {
			const headers = {
				'x-api-key': xAPIKey || '',
			} as Record<string, string>;
			const tokens = get();
			if (tokens) {
				headers.Authorization = `Bearer ${tokens.token}`;
			}
			restClient!
				.delete(`${base}${endpoint}`, {
					headers,
				})
				.then((response) => {
					resolve(response);
				})
				.catch((error) => {
					reject(error);
				});
		});
	}, []);

	const uploadFromData = useCallback((data: any, override?: Partial<FileArgs>) => {
		const merged = {
			...args,
			...(override || {}),
		};
		const { onStart, onUpload, onError, onComplete } = merged;
		// extract file to be uploaded from form data
		const filesPos = Object.keys(data).reduce<string[]>((acc, k) => {
			const value = data[k];
			if (isArray(value)) {
				value.forEach((i: any, kk: number) => {
					if (i.content && i.content instanceof Blob) {
						acc.push(`${k}[${kk}]`);
					}
				});
			}
			if (value && value.content && value.content instanceof Blob) {
				acc.push(k);
			}
			return acc;
		}, []);
		if (onStart) onStart(filesPos.map((key) => _get(data, key)));

		// finally send files and modify data
		const q = filesPos.reduce(async (acc, key) => {
			const data = await acc;
			try {
				const res = await sendFile(_get(data, key), override);
				_set(data, key, res);
				if (onUpload) onUpload(_get(data, key));
			} catch (e) {
				_set(data, key, e);
				if (onError) onError(_get(data, key), e);
			}
			return data;
		}, Promise.resolve(data));

		// resolve new data
		return new Promise((resolve, reject) => {
			q.then((newData) => {
				if (onComplete) onComplete();
				resolve(newData);
			});
		});
	}, []);

	const getDownloadFileUrl = useCallback((file: FileWithContents, override?: Partial<FileArgs>) => {
		const { base, endpoint } = getRequestArgs(override);
		return `${base}${endpoint}/{fileUniqueId}`;
	}, []);

	return {
		downloadFile,
		sendFile,
		removeFile,
		uploadFromData,
		getDownloadFileUrl,
	};
}
